import { useRef, useState } from "react";
import { Smile, Send } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const EMOJIS = "😂🤣😭😍🥰😎🤔😱😡🤬💀👻🤡👽🔥💯✨🎉🙌👏👍👎🤘🙏💪👀❤️💔💩🍿🎬🎵🎸🥁🍕🌮🍺🦖🐼🐻".match(/\p{Extended_Pictographic}\uFE0F?/gu);

export default function CommentComposer({ onSubmit, initial = "", placeholder = "Escribe un comentario...", submitLabel = "Comentar", onCancel, accent, autoFocus, testId = "comment-composer" }) {
  const [text, setText] = useState(initial);
  const [sending, setSending] = useState(false);
  const ref = useRef(null);

  const insertEmoji = (emoji) => {
    const el = ref.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    setText(text.slice(0, start) + emoji + text.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + emoji.length, start + emoji.length);
    });
  };

  const submit = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    const ok = await onSubmit(text.trim());
    setSending(false);
    if (ok && !initial) setText("");
  };

  return (
    <div className="flex-1" data-testid={testId}>
      <textarea
        ref={ref}
        value={text}
        autoFocus={autoFocus}
        onChange={(e) => setText(e.target.value.slice(0, 1000))}
        placeholder={placeholder}
        rows={2}
        className="w-full bg-white/5 border border-white/15 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-white/40 resize-none"
        data-testid={`${testId}-input`}
      />
      <div className="flex items-center justify-between mt-2">
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="p-1.5 text-gray-400 hover:text-white transition-colors" data-testid={`${testId}-emoji-btn`}>
              <Smile className="w-5 h-5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-2 bg-[#141414] border-white/15" align="start">
            <div className="grid grid-cols-8 gap-1">
              {EMOJIS.map((e) => (
                <button key={e} type="button" onClick={() => insertEmoji(e)} className="text-xl p-1 rounded hover:bg-white/10" data-testid="emoji-option">
                  {e}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-500">{text.length}/1000</span>
          {onCancel && (
            <button type="button" onClick={onCancel} className="text-sm text-gray-400 hover:text-white px-3 py-1.5" data-testid={`${testId}-cancel`}>
              Cancelar
            </button>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={!text.trim() || sending}
            className="flex items-center gap-1.5 text-sm font-bold text-black px-4 py-1.5 rounded-full disabled:opacity-40 transition-opacity"
            style={{ backgroundColor: accent }}
            data-testid={`${testId}-submit`}
          >
            <Send className="w-3.5 h-3.5" /> {sending ? "..." : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
